import { getConfig } from '../../../config';
import { levoHttp } from '../../../utils/http';
import { create as createQueryParam } from '../../../utils/queryParam';
import endpoints from '../constants/endpoints';
import {
  ApiResponse,
  CandidateDetailData,
  CandidateDocumentLinks,
  CandidatesListData,
  CandidateTimelineData,
  LocationsByRegionData,
  RecruitStageData,
} from '../types/api';

/**
 * Replace URL parameters with actual values
 *
 * @param {string} url - The URL template with parameter placeholders
 * @param {Record<string, string | number>} params - Object containing parameter key-value pairs
 *
 * @returns {string} URL with parameters replaced
 */
const replaceUrlParams = (url: string, params: Record<string, string | number>): string => {
  let result = url;

  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, value.toString());
  });

  return result;
};

/**
 * Get the list of candidates with optional query parameters
 *
 * @param {Record<string, any>} [query={}] - Query parameters for filtering and pagination
 *
 * @returns {Promise<ApiResponse<CandidatesListData>>} Promise that resolves to the candidates list response
 */
export const getCandidatesList = (
  query: Record<string, any> = {}
): Promise<ApiResponse<CandidatesListData>> => {
  const param = createQueryParam(query);
  const url = `${getConfig().authBaseUrl}${endpoints.candidateList}${param}`;
  return levoHttp.get(url);
};

/**
 * Get detailed information for a specific candidate
 *
 * @param {number|string} candidateId - The candidate ID
 *
 * @returns {Promise<ApiResponse<CandidateTimelineData>>} Promise that resolves to the candidate detail response
 */
export const getCandidateDetail = (
  candidateId: number | string
): Promise<ApiResponse<CandidateDetailData>> => {
  const url = replaceUrlParams(endpoints.candidateDetail, { id: candidateId });
  const fullUrl = `${getConfig().authBaseUrl}${url}`;
  return levoHttp.get(fullUrl);
};

/**
 * Get the timeline for a specific candidate
 *
 * @param {number|string} candidateId - The candidate ID
 *
 * @returns {Promise<ApiResponse<CandidateTimelineData>>} Promise that resolves to the candidate timeline response
 */
export const getCandidateTimeline = (
  candidateId: number | string
): Promise<ApiResponse<CandidateTimelineData>> => {
  const url = replaceUrlParams(endpoints.candidateTimeline, { id: candidateId });
  const fullUrl = `${getConfig().authBaseUrl}${url}`;
  return levoHttp.get(fullUrl);
};

/**
 * Resend a stage link for a specific candidate
 *
 * @param {number|string} candidateId - The candidate ID
 * @param {Record<string, any>} payload - The payload data for resending the stage link
 *
 * @returns {Promise<ApiResponse<any>>} Promise that resolves to the resend stage link response
 */
export const resendStageLink = (
  candidateId: number | string,
  payload: Record<string, any>
): Promise<ApiResponse<any>> => {
  const url = replaceUrlParams(endpoints.triggerStageLink, { id: candidateId });
  const fullUrl = `${getConfig().authBaseUrl}${url}`;
  return levoHttp.post(fullUrl, payload);
};

/**
 * Get document links for a specific candidate
 *
 * @param {number|string} candidateId - The candidate ID
 *
 * @returns {Promise<ApiResponse<CandidateDocumentLinks>>} Promise that resolves to the candidate document links response
 */
export const getCandidateDocumentLinks = (
  candidateId: number | string
): Promise<ApiResponse<CandidateDocumentLinks>> => {
  const url = replaceUrlParams(endpoints.candidateDocumentLinks, { id: candidateId });
  const fullUrl = `${getConfig().authBaseUrl}${url}`;
  return levoHttp.get(fullUrl);
};

/**
 * Get locations grouped by region
 *
 * @returns {Promise<ApiResponse<LocationsByRegionData>>} Promise that resolves to the locations by region response
 */
export const getLocationsByRegion = (): Promise<ApiResponse<LocationsByRegionData>> => {
  const url = `${getConfig().authBaseUrl}${endpoints.locationsByRegion}`;
  return levoHttp.get(url);
};

/**
 * Get the stages for the recruit flow
 *
 * @returns {Promise<ApiResponse<RecruitStageData>>} Promise that resolves to the stages for the recruit flow
 */
export const getRecruitStages = (): Promise<ApiResponse<RecruitStageData>> => {
  const url = `${getConfig().authBaseUrl}${endpoints.recruitStages}`;
  return levoHttp.get(url);
};
